import React, {useEffect, useRef, useState} from "react";
import devSearchImage from "./assets/images/devsearch.png";
import "./App.scss";

const skillGroups = [
  {
    title: "Backend",
    intro: "The part I reach for first.",
    skills: ["C#", ".NET", "ASP.NET Core", "Entity Framework", "REST APIs"]
  },
  {
    title: "Frontend",
    intro: "Clear interfaces, built for real use.",
    skills: ["React", "TypeScript", "JavaScript", "Blazor", "HTML & CSS"]
  },
  {
    title: "Azure",
    intro: "Connecting and running cloud applications.",
    skills: [
      "Azure Functions",
      "Service Bus",
      "Key Vault",
      "Application Insights",
      "Azure SQL"
    ]
  },
  {
    title: "Quality & support",
    intro: "Because shipping is only the beginning.",
    skills: [
      "xUnit",
      "NUnit",
      "Root-cause analysis",
      "Production support",
      "Git & code review"
    ]
  }
];

const strengths = [
  {
    title: "Build the whole feature",
    text: "I enjoy following a problem from the first conversation through the API, interface, data and final release."
  },
  {
    title: "Find what actually broke",
    text: "When production is unhappy, I work through logs, code and data methodically, then make the smallest safe fix."
  },
  {
    title: "Leave the code better",
    text: "Useful tests, readable changes and a little less complexity than I found are all part of finishing the job."
  }
];

const CONTACT_API = "https://contact-api.robaelshazly.dev";
const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "1x00000000000000000000BB"
    : "0x4AAAAAAEDIxMYH6AVHHuxC");

let turnstileScriptPromise;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error("Unable to load verification"));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}

function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    website: ""
  });
  const [status, setStatus] = useState("idle");
  const [notice, setNotice] = useState("");
  const widgetContainer = useRef(null);
  const widgetId = useRef(null);
  const pendingVerification = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    let cancelled = false;
    const prepare = () => {
      loadTurnstile()
        .then(turnstile => {
          if (cancelled || !turnstile || widgetId.current !== null) return;
          widgetId.current = turnstile.render(widgetContainer.current, {
            sitekey: TURNSTILE_SITE_KEY,
            size: "invisible",
            execution: "execute",
            action: "contact",
            callback: token => {
              if (pendingVerification.current) {
                window.clearTimeout(pendingVerification.current.timerId);
                pendingVerification.current.resolve(token);
                pendingVerification.current = null;
              }
            },
            "error-callback": () => {
              if (pendingVerification.current) {
                window.clearTimeout(pendingVerification.current.timerId);
                pendingVerification.current.reject(
                  new Error("Verification failed")
                );
                pendingVerification.current = null;
              }
            },
            "expired-callback": () => {
              if (pendingVerification.current) {
                window.clearTimeout(pendingVerification.current.timerId);
                pendingVerification.current.reject(
                  new Error("Verification expired")
                );
                pendingVerification.current = null;
              }
            }
          });
        })
        .catch(() => {
          // A clear message is shown only if the visitor tries to submit.
        });
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(prepare);
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timerId = window.setTimeout(prepare, 1);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, []);

  const handleChange = event => {
    const {name, value} = event.target;
    setForm(current => ({...current, [name]: value}));
  };

  const getVerificationToken = async () => {
    if (!TURNSTILE_SITE_KEY) {
      throw new Error("Verification is not configured");
    }

    const turnstile = await loadTurnstile();
    if (widgetId.current === null) {
      throw new Error("Verification is still loading");
    }

    turnstile.reset(widgetId.current);
    return new Promise((resolve, reject) => {
      const timerId = window.setTimeout(() => {
        pendingVerification.current = null;
        reject(new Error("Verification timed out"));
      }, 12_000);
      pendingVerification.current = {resolve, reject, timerId};
      turnstile.execute(widgetId.current);
    });
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (status === "sending") return;

    // Quietly absorb simple bot submissions without sending email.
    if (form.website) {
      setStatus("sent");
      setNotice("Thanks. Your message has been sent.");
      return;
    }

    setStatus("sending");
    setNotice("Sending your message...");

    try {
      const token = await getVerificationToken();
      const response = await fetch(CONTACT_API, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          "cf-turnstile-response": token
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || "Unable to send your message");
      }

      setForm({name: "", email: "", message: "", website: ""});
      setStatus("sent");
      setNotice("Thanks. Your message has been sent.");
    } catch (error) {
      setStatus("error");
      setNotice(
        error.message === "Too many messages. Please try again shortly."
          ? error.message
          : "That did not send. Please try again or email me directly."
      );
    } finally {
      if (window.turnstile && widgetId.current !== null) {
        window.turnstile.reset(widgetId.current);
      }
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="contact-fields">
        <label>
          <span>Your name</span>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            autoComplete="name"
            maxLength="100"
            required
          />
        </label>
        <label>
          <span>Email address</span>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
            inputMode="email"
            maxLength="254"
            required
          />
        </label>
      </div>
      <label>
        <span>What would you like to talk about?</span>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          rows="5"
          maxLength="4000"
          required
        />
      </label>
      <label className="contact-honeypot" aria-hidden="true">
        Website
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </label>
      <div
        className="turnstile-background"
        ref={widgetContainer}
        aria-hidden="true"
      />
      <div className="contact-form-footer">
        <button type="submit" disabled={status === "sending"}>
          {status === "sending" ? "Sending..." : "Send message"}
          <span aria-hidden="true">→</span>
        </button>
        <p className="contact-privacy">
          Your details are used only to reply.
        </p>
      </div>
      <p
        className={`contact-status contact-status-${status}`}
        role="status"
        aria-live="polite"
      >
        {notice}
      </p>
    </form>
  );
}

function ExternalLink({href, children, className = ""}) {
  return (
    <a className={className} href={href} target="_blank" rel="noreferrer">
      {children}
      <span className="arrow" aria-hidden="true">
        ↗
      </span>
    </a>
  );
}

function App() {
  return (
    <div className="site-shell portfolio-v2">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="site-header">
        <a className="brand" href="#top" aria-label="Roba Elshazly home">
          Roba<span>.</span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#skills">Skills</a>
          <a href="#work">Work</a>
          <a href="#about">About</a>
        </nav>
        <a
          className="resume-link"
          href="/docs/Roba_Elshazly_Software_Engineer_Resume.pdf"
          download
        >
          Download CV
        </a>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="hero-copy">
            <p className="hello">Hello, I&apos;m Roba <span aria-hidden="true">👋</span></p>
            <h1>
              I make software that stays useful{" "}
              <em>after it ships.</em>
            </h1>
            <p className="hero-lead">
              I&apos;m a Brisbane-based software engineer who likes turning
              complicated business problems into clear, dependable
              applications, mostly with .NET, Azure and React.
            </p>
            <div className="hero-actions">
              <a className="button button-dark" href="#skills">
                See what I work with
              </a>
              <a className="text-link" href="mailto:hello@robaelshazly.dev">
                Say hello <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <aside className="hero-note" aria-label="A note from Roba">
            <span className="tape" aria-hidden="true" />
            <p className="note-label">The short version</p>
            <p>
              I like understanding how a system really works, asking the useful
              questions and carrying a change all the way through.
            </p>
            <p>
              Features are fun. Fixing the awkward edge cases is usually where
              the interesting work begins.
            </p>
            <span className="signature">Roba</span>
          </aside>
        </section>

        <section className="skills-section" id="skills">
          <div className="section-heading">
            <p className="section-label">My toolkit</p>
            <h2>What I work with</h2>
            <p>
              I&apos;m most at home across the full stack, especially where
              application code, cloud services and production support overlap.
            </p>
          </div>

          <div className="skill-groups">
            {skillGroups.map(group => (
              <article className="skill-group" key={group.title}>
                <div>
                  <h3>{group.title}</h3>
                  <p>{group.intro}</p>
                </div>
                <ul>
                  {group.skills.map(skill => (
                    <li key={skill}>{skill}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="strengths-section" id="about">
          <div className="section-heading">
            <p className="section-label">How I help</p>
            <h2>More than a list of technologies</h2>
          </div>
          <div className="strength-list">
            {strengths.map((strength, index) => (
              <article key={strength.title}>
                <span>0{index + 1}</span>
                <h3>{strength.title}</h3>
                <p>{strength.text}</p>
              </article>
            ))}
          </div>
          <p className="ai-note">
            I also use Codex as an engineering tool for investigation,
            implementation and refactoring, while keeping review, testing and
            technical judgement firmly human.
          </p>
        </section>

        <section className="work-section" id="work">
          <div className="work-copy">
            <p className="section-label">A project of my own</p>
            <h2>DevSearch</h2>
            <p className="work-intro">
              A full-stack marketplace that brings employers and job seekers
              together.
            </p>
            <p>
              I built profile and job-posting flows, JWT authentication,
              document handling and multi-stage application tracking. It was a
              practical lesson in designing a product from both sides of the
              user journey.
            </p>
            <p className="stack-line">
              React · Node.js · Express · MongoDB · AWS S3
            </p>
            <div className="work-links">
              <ExternalLink href="https://devsearch.robaelshazly.dev">
                Visit DevSearch
              </ExternalLink>
              <ExternalLink href="https://github.com/robaElshazly/devsearch">
                View the code
              </ExternalLink>
            </div>
          </div>
          <a
            className="project-preview"
            href="https://devsearch.robaelshazly.dev"
            target="_blank"
            rel="noreferrer"
            aria-label="Open DevSearch"
          >
            <img
              src={devSearchImage}
              alt="DevSearch landing page"
            />
          </a>
        </section>

        <section className="learning-section" aria-label="Learning and credentials">
          <div>
            <p className="section-label">A little background</p>
            <h2>Always learning, on purpose.</h2>
          </div>
          <div className="learning-items">
            <p>
              <strong>Microsoft Certified</strong>
              Azure Developer Associate
            </p>
            <p>
              <strong>Coder Academy</strong>
              Web Development Bootcamp
            </p>
            <p>
              <strong>Foundation</strong>
              Computer Science &amp; Mathematics
            </p>
          </div>
        </section>

        <section className="contact-section" id="contact">
          <div className="contact-intro">
            <p className="section-label">Get in touch</p>
            <h2>Have a problem worth solving?</h2>
            <p>
              I&apos;m open to software engineering opportunities and good
              conversations about building dependable products.
            </p>
            <a className="contact-email" href="mailto:hello@robaelshazly.dev">
              hello@robaelshazly.dev <span aria-hidden="true">→</span>
            </a>
            <div className="social-links">
              <ExternalLink href="https://www.linkedin.com/in/roba-elshazly/">
                LinkedIn
              </ExternalLink>
              <ExternalLink href="https://github.com/robaElshazly">
                GitHub
              </ExternalLink>
            </div>
          </div>
          <ContactForm />
        </section>
      </main>

      <footer>
        <p>© {new Date().getFullYear()} Roba Elshazly</p>
        <a href="#top">Back to the top ↑</a>
      </footer>
    </div>
  );
}

export default App;
